import {
  IconFilter,
  ActiveFilter,
  MagicTableFilterConfig,
  MagicTableProps
} from '@sequencehq/core-components'
import { useQuery } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'
import {
  selectedCustomFieldColumnsAtom,
  selectedCustomFieldFiltersAtom
} from 'modules/BillingSchedules/billingSchedules.atoms'
import {
  BillingSchedulesFilters,
  BillingSchedulesView
} from 'modules/BillingSchedules/types'
import { useSetAtom } from 'jotai'
import { useEffect, useMemo, useState } from 'react'
import type { DashboardApi20240730 } from '@sequencehq/api/dashboard/v20240730'

type BillingSchedule = DashboardApi20240730.GetBillingSchedules.BillingSchedule

/**
 * Fetches the set of billing schedule custom field keys and returns:
 * - `allCustomFieldKeys`: the raw list of known keys (used by
 *   `useBillingScheduleCustomFieldObjectPropertyColumns` to build the
 *   columns config).
 * - `customFieldFilters`: a text filter per key, surfaced as top-level
 *   entries in the MagicTable filter menu. Spread into the consumer's
 *   filter array before threading it through
 *   `useLinkMagicTableWithSearchParams` so the URL parser recognises
 *   each `customFields.<key>` param.
 *
 * Split from the columns hook because the filter list must be available
 * before `useLinkMagicTableWithSearchParams` runs, but the columns config
 * depends on the resulting `activeFilters` — so the two pieces sit on
 * either side of that call.
 */
export const useBillingScheduleCustomFieldFilters = () => {
  const customFieldKeysQuery = useQuery(
    dashboard20240730Client.getBillingScheduleCustomFieldKeys
  )
  const allCustomFieldKeys = useMemo(
    () => customFieldKeysQuery.data?.items ?? [],
    [customFieldKeysQuery.data?.items]
  )

  const customFieldFilters = useMemo<
    MagicTableFilterConfig<BillingSchedulesFilters>[]
  >(
    () =>
      allCustomFieldKeys.map(key => ({
        type: 'text' as const,
        paramName: `customFields.${key}`,
        label: key,
        icon: IconFilter
      })),
    [allCustomFieldKeys]
  )

  return { allCustomFieldKeys, customFieldFilters }
}

type ObjectPropertyColumnsResult = NonNullable<
  MagicTableProps<BillingSchedulesView>['objectPropertyColumns']
>

/**
 * Builds the `objectPropertyColumns` config that drives the custom field
 * columns on the billing schedules MagicTable:
 * - Pulls values off each schedule's `customFields` map.
 * - Forces a column to render for any custom field with an active filter
 *   so its values are immediately visible.
 * - Maps each key to a sortable backend property (`customFields.<key>`).
 * - Mirrors the active filter and selected column state into the jotai
 *   atoms consumed by the CSV export hook.
 *
 * Pair with `useBillingScheduleCustomFieldFilters`. Pass `activeFilters`
 * after it has been derived from the filter array (which must include
 * the `customFieldFilters` from the filters hook).
 */
export const useBillingScheduleCustomFieldObjectPropertyColumns = ({
  allCustomFieldKeys,
  activeFilters
}: {
  allCustomFieldKeys: string[]
  activeFilters: ActiveFilter[]
}): ObjectPropertyColumnsResult => {
  const [customFieldKeysSearch, setCustomFieldKeysSearch] = useState('')
  const filteredCustomFieldKeys = useMemo(
    () =>
      allCustomFieldKeys.filter(key =>
        key.toLowerCase().includes(customFieldKeysSearch.toLowerCase())
      ),
    [allCustomFieldKeys, customFieldKeysSearch]
  )

  const setSelectedCustomFieldColumns = useSetAtom(
    selectedCustomFieldColumnsAtom
  )

  // The set of custom field keys that currently have an active filter
  // value. Used to (a) mirror filters to the CSV export hook, and (b)
  // ensure each filtered key is always shown as a column.
  const activeCustomFieldFilters = useMemo(
    () =>
      activeFilters.reduce<Record<string, string>>((acc, filter) => {
        if (filter.type !== 'text' || !filter.value) return acc
        if (!filter.paramName.startsWith('customFields.')) return acc
        const key = filter.paramName.slice('customFields.'.length)
        if (!key) return acc
        return { ...acc, [key]: filter.value }
      }, {}),
    [activeFilters]
  )

  const filteredCustomFieldKeysForColumns = useMemo(
    () => Object.keys(activeCustomFieldFilters),
    [activeCustomFieldFilters]
  )

  const setSelectedCustomFieldFilters = useSetAtom(
    selectedCustomFieldFiltersAtom
  )
  useEffect(() => {
    setSelectedCustomFieldFilters(activeCustomFieldFilters)
  }, [activeCustomFieldFilters, setSelectedCustomFieldFilters])

  return useMemo<ObjectPropertyColumnsResult>(
    () => ({
      allEventProperties: filteredCustomFieldKeys,
      getObject: (model: BillingSchedule) => model.customFields ?? {},
      searchFunc: (query: string) => {
        setCustomFieldKeysSearch(query)
        return allCustomFieldKeys.map(key => ({
          label: key,
          value: key
        }))
      },
      menuLabel: 'Custom fields',
      searchPlaceholder: 'Search custom fields',
      valueAsPill: false,
      onSelectedChange: setSelectedCustomFieldColumns,
      requiredKeys: filteredCustomFieldKeysForColumns,
      // Custom field keys are surfaced as top-level entries in the add
      // column / view settings menus rather than under a sub-menu. The
      // MagicTable sub-menu mode is still supported for other consumers
      // (e.g. usage events) that opt out of this flag.
      inlineInAddColumnMenu: true,
      // Server accepts `customFields.<key>` as a sort key; map each
      // custom-field column to its corresponding sort property so the
      // column headers offer Sort ascending / descending alongside the
      // static columns.
      sortPropertyForKey: (key: string) => `customFields.${key}`
    }),
    [
      allCustomFieldKeys,
      filteredCustomFieldKeys,
      filteredCustomFieldKeysForColumns,
      setSelectedCustomFieldColumns
    ]
  )
}
